import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Button, TextInput } from '@mantine/core';

interface SearchFilterProps {
  value: string;
  onChange: (value: string) => void;
}

export const SearchFilter: React.FC<SearchFilterProps> = ({ value, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggle = () => {
    if (isExpanded) {
      // If we're collapsing, clear the search and close
      setSearchValue('');
      onChange('');
      setIsExpanded(false);
    } else {
      // If we're expanding, focus the input
      setIsExpanded(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleToggle();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSearchValue('');
    onChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Update internal state when prop changes
  useEffect(() => {
    setSearchValue(value);
    // Expand if there's a value, collapse if value is cleared externally
    if (value) {
      setIsExpanded(true);
    } else if (value === '' && searchValue !== '') {
      // Value was cleared externally (like from clear filters), so collapse
      setIsExpanded(false);
    }
  }, [value, searchValue]);

  if (isExpanded) {
    return (
      <div style={{ position: 'relative', width: '200px' }}>
        <TextInput
          ref={inputRef}
          value={searchValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder="Search todos..."
          size="sm"
          leftSection={<Search size={12} />}
          rightSection={
            searchValue ? (
              <X
                size={12}
                onClick={handleClear}
                style={{ cursor: 'pointer', opacity: 0.7 }}
                data-clear-button="true"
              />
            ) : null
          }
          styles={{
            input: {
              height: '32px', // Match Button compact-sm height
              fontSize: '13px',
              paddingLeft: '32px',
              paddingRight: searchValue ? '32px' : '12px',
            }
          }}
          onBlur={(e) => {
            // Don't collapse if clicking on the clear button (X)
            if (e.relatedTarget?.closest('[data-clear-button]')) {
              return;
            }
            // Only collapse when losing focus if there's no text in the field
            if (!searchValue.trim()) {
              setTimeout(() => {
                setIsExpanded(false);
              }, 100);
            }
          }}
        />
      </div>
    );
  }

  return (
    <Button
      variant="default"
      size="compact-sm"
      onClick={handleToggle}
      title="Search todos"
      px={8}
    >
      {value ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Search size={12} />
          <X 
            size={12} 
            onClick={(e) => {
              e.stopPropagation();
              setSearchValue('');
              onChange('');
            }}
            style={{ cursor: 'pointer', opacity: 0.7 }}
          />
        </span>
      ) : (
        <Search size={12} />
      )}
    </Button>
  );
};